<template>
  <div
    v-if="offer.versions.length"
    class="d-contents"
    :class="{ 'offers-column_disabled': isVersionOldOrArchived }"
  >
    <div class="offers-table-cell offers-table-cell_versions" :style="{ 'grid-row': '1 / 2' }">
      <TenderVersionDropdown
        :selected-version="selectedVersion"
        :team-name="teamName"
        :versions="offer.versions"
        @change-selected-version="(version) => $emit('change-version', version)"
      />
    </div>
    <div class="offers-table-cell offers-table-cell_details" :style="{ 'grid-row': '2 / 3' }">
      <InfoTooltip small container-class="cell__btn" bottom>
        <template #element>
          <Button type="func" :loading="exportXlsxLoading" @click="handleExport">
            <Icon size="16" type="download" />
          </Button>
        </template>
        <span>{{ $t('Export offer to Excel') }}</span>
      </InfoTooltip>
      <button type="button" class="cell__btn spacex-6" @click="handleDescriptionOpening">
        <Icon size="16" type="list" /><span>{{ $t('Description') }}</span>
      </button>
    </div>
    <router-link
      v-for="(value, cellsIndex) in tenderOffers.lists"
      :key="cellsIndex"
      v-slot="{ navigate }"
      custom
      :to="{
        name: 'tender-list',
        params: { tenderListId: listId(cellsIndex) },
        query: {
          version: `${offer.id}_${selectedVersion.id}`,
        },
      }"
    >
      <span
        :style="{ 'grid-row': `${cellsIndex + 3} / ${cellsIndex + 4}` }"
        class="offers-table-cell offers-table-cell_positions spacex-4 link"
        :class="{ 'offers-table-cell_disabled': isVersionOldOrArchived }"
        @click="navigate"
      >
        <span v-if="cellValue(cellsIndex)" class="table-cell-currency">
          {{ currencySymbol }}
        </span>
        <span v-if="cellValue(cellsIndex)">
          {{ addSpaces(cellValue(cellsIndex)) }}
        </span>
      </span>
    </router-link>
    <div
      class="offers-table-cell offers-table-cell_taxes"
      :style="{ 'grid-row': `${rowsCount + 4} / ${rowsCount + 5}` }"
    >
      <TenderTaxes :taxes="selectedVersion.taxes" :currency-symbol="currencySymbol" />
    </div>
    <div
      class="offers-table-cell offers-table-cell_files"
      :style="{ 'grid-row': `${rowsCount + 5} / ${rowsCount + 6}` }"
    >
      <OfferFiles :offer="selectedVersion" download-only />
    </div>
  </div>
</template>

<script>
  import TenderVersionDropdown from '@/components/tender/TenderVersionDropdown';
  import TenderTaxes from '@/components/tender/TenderTaxes';
  import addSpaces from '@/utils/formatting/add-spaces';
  import OfferFiles from '@/containers/incoming-tender/incoming-tender-offers-table/OfferFiles';
  import downloadByLink from '@/utils/download-by-link';
  import EditDescriptionModal from '@/containers/incoming-tender/incoming-tender-offers-table/EditDescriptionModal';
  import { mapActions, mapState } from 'vuex';
  import Icon from '@/components/ui/Icon';
  import Button from '@/components/ui-v2/Button';
  import InfoTooltip from '@/components/ui/InfoTooltip';

  export default {
    name: 'TenderOffersColumn',
    components: {
      TenderTaxes,
      TenderVersionDropdown,
      OfferFiles,
      Icon,
      Button,
      InfoTooltip,
    },
    props: {
      tenderOffers: {
        type: Object,
        required: true,
      },
      columnIndex: {
        type: Number,
        required: true,
      },
      rowsCount: {
        type: Number,
        required: true,
      },
      offer: {
        type: Object,
        required: true,
      },
      selectedVersion: {
        type: Object,
        required: true,
      },
    },
    data() {
      return {
        exportXlsxLoading: false,
      };
    },
    computed: {
      ...mapState('global', ['currencySymbols']),
      ...mapState('tender', ['tenderInfo']),
      currencySymbol() {
        return this.currencySymbols[this.tenderInfo.currency];
      },
      teamName() {
        return this.offer?.contractor_team?.name || '';
      },
      isVersionOldOrArchived() {
        return this.selectedVersion.status === 'old' || this.selectedVersion.status === 'archived';
      },
    },
    methods: {
      ...mapActions('modal', ['openModal']),
      handleDescriptionOpening() {
        this.openModal({
          component: EditDescriptionModal,
          context: {
            offer: this.selectedVersion,
            readOnly: true,
          },
        });
      },
      addSpaces,
      cellValue(rowIndex) {
        return this.tenderOffers.list_sums[
          `${this.tenderOffers.lists[rowIndex].id}_${this.selectedVersion.id}`
        ];
      },
      listId(rowIndex) {
        return this.tenderOffers.lists[rowIndex].id;
      },
      changeSelectedVersion(id) {
        this.selectedVersion = this.offer.versions.find((version) => version.id === id);
      },
      getFileName() {
        // (название тендера)-(название компании в оффере)-v(номер версии)-(год)-(месяц)-(день)
        const tenderName = this.tenderInfo?.name || this.$t('Tender');
        const contractorName = this.offer?.contractor_team?.name || '';
        const selectedVersion = this.selectedVersion?.name ? `v${this.selectedVersion.name}` : '';
        // YYYY-MM-DD
        const createdDate = this.offer?.created_at
          ? new Date(this.offer.created_at).toISOString().slice(0, 10)
          : '';
        const nameElements = [tenderName, contractorName, selectedVersion, createdDate];
        return nameElements.filter((elem) => elem || false).join('-') + '.xlsx';
      },
      async handleExport() {
        this.exportXlsxLoading = true;
        await downloadByLink({
          link: `/tenders/${this.$route.params.tenderId}/offers/${this.offer.id}/xlsx/`,
          query: {
            version_id: this.selectedVersion.id,
          },
          fileName: this.getFileName(),
        });
        this.exportXlsxLoading = false;
      },
    },
  };
</script>
